import { revisedSalary, earnedTutorPay, projectedTutorPay, additionalParentFee } from "utils/feeRevisionPayroll";
import React from "react";
import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArgonButton from "components/ArgonButton";
import {
  FormControl,
  Select,
  LinearProgress,
  MenuItem,
  InputLabel,
  Chip,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import { Person, Refresh, FilterList } from "@mui/icons-material";
// MUI

import breakpoints from "assets/theme/base/breakpoints";

// Data
import TutorsPayrollViewStudentTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollViewStudentTableData";

import CreditMappingViewTableData from "Views/Activities/CreditMapping/data/creditMappingViewTableData";
import CreditMappingViewCreditTableData from "Views/Activities/CreditMapping/data/creditMappingViewCreditTableData";
import TutorsParollViewPaymentOrderTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollViewPaymentOrderTableData";
import PropTypes from "prop-types";

// import ReferralTableData from "Views/Activities/Referral/data/ReferralTableData"
import { useState, useEffect } from "react";
import { set } from "date-fns";
import Dropdown from "components/CustomDropdown/Dropdown";
import ArgonInput from "components/ArgonInput";
import axios from "utils/api";

function payroll({ classData, payrollCycles = [] }) {
  const currentPayrollCycles = Array.isArray(payrollCycles) ? payrollCycles : [];
  const oldPayrollCycles = Array.isArray(classData?.oldTutorPaymentCycles)
    ? classData.oldTutorPaymentCycles
    : [];
  const hasOldTutorData = Boolean(
    classData?.oldTutorDetails || oldPayrollCycles.length > 0 || classData?.oldTutorCompletedClasses
  );
  const [activeTab, setActiveTab] = useState("current");
  const [selectedCycleByTab, setSelectedCycleByTab] = useState({ current: "", old: "" });
  const [currentPayrollByTab, setCurrentPayrollByTab] = useState({
    current: null,
    old: null,
  });
  const [attendanceEntries, setAttendanceEntries] = useState([]);

  const toSafeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const resolveClassCycle = (cycleNumber, tab = activeTab) => {
    const cycles = Array.isArray(classData?.cycles) ? classData.cycles : [];
    if (cycles.length === 0) return null;

    const numericCycleNumber = Number(cycleNumber);
    if (Number.isFinite(numericCycleNumber) && numericCycleNumber > 0) {
      return cycles[numericCycleNumber - 1] || null;
    }

    const payrollCycleName =
      tab === "old"
        ? classData?.oldTutorDetails?.currentCycle || classData?.oldTutorDetails?.cycleName
        : classData?.currentCycle;

    if (payrollCycleName) {
      const byName = cycles.find((cycle) => String(cycle?.name || "").trim() === String(payrollCycleName).trim());
      if (byName) return byName;
    }

    return cycles.find((cycle) => String(cycle?.status || "").toUpperCase() === "ONGOING") || cycles[0] || null;
  };

  const getCycleSessions = (cycleData) =>
    toSafeNumber(cycleData?.totalNoOfClasses || cycleData?.classes || cycleData?.sessionCount || 0);

  const getTotalCycleAmount = (cycles, fieldName, fallbackValue = 0) => {
    if (!Array.isArray(cycles) || cycles.length === 0) return toSafeNumber(fallbackValue);
    const total = cycles.reduce((sum, item) => sum + toSafeNumber(item?.[fieldName]), 0);
    return total > 0 ? total : toSafeNumber(fallbackValue);
  };

  const resolveDefaultCycle = (cycles) => {
    if (!Array.isArray(cycles) || cycles.length === 0) return null;

    const now = new Date();
    const activeStatuses = ["ACTIVE", "PENDING", "APPROVED", "PROCESSING"];
    const toValidDate = (value) => {
      if (!value) return null;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };
    const getStartDate = (cycle) =>
      toValidDate(cycle?.cycleStartDate || cycle?.startDate || cycle?.start || cycle?.createdAt);
    const getEndDate = (cycle) =>
      toValidDate(cycle?.cycleEndDate || cycle?.endDate || cycle?.end || cycle?.updatedAt);

    const activeByDate = cycles.find((cycle) => {
      const startDate = getStartDate(cycle);
      const endDate = getEndDate(cycle);
      return startDate && endDate && startDate <= now && now <= endDate;
    });

    if (activeByDate) return activeByDate;

    const activeByStatus = cycles.find((cycle) =>
      activeStatuses.includes(String(cycle?.status || "").toUpperCase())
    );

    if (activeByStatus) return activeByStatus;

    const upcomingCycle = [...cycles]
      .filter((cycle) => {
        const startDate = getStartDate(cycle);
        return startDate && startDate >= now;
      })
      .sort((a, b) => getStartDate(a) - getStartDate(b))[0];

    return upcomingCycle || cycles[0] || null;
  };

  // Set default payroll data when payrollCycles are loaded
  useEffect(() => {
    if (currentPayrollCycles.length > 0) {
      const defaultCycle = resolveDefaultCycle(currentPayrollCycles);
      setCurrentPayrollByTab((prev) => ({ ...prev, current: defaultCycle }));
      setSelectedCycleByTab((prev) => ({ ...prev, current: defaultCycle?._id || "" }));
    } else {
      setCurrentPayrollByTab((prev) => ({ ...prev, current: null }));
      setSelectedCycleByTab((prev) => ({ ...prev, current: "" }));
    }
  }, [currentPayrollCycles]);

  useEffect(() => {
    if (oldPayrollCycles.length > 0) {
      const defaultCycle = resolveDefaultCycle(oldPayrollCycles);
      setCurrentPayrollByTab((prev) => ({ ...prev, old: defaultCycle }));
      setSelectedCycleByTab((prev) => ({ ...prev, old: defaultCycle?._id || "" }));
    } else {
      setCurrentPayrollByTab((prev) => ({ ...prev, old: null }));
      setSelectedCycleByTab((prev) => ({ ...prev, old: "" }));
    }
  }, [oldPayrollCycles]);

  useEffect(() => {
    if (classData?._id) {
      const fetchAttendance = async () => {
        try {
          const response = await axios.get("/attendance", {
            params: { classSessionId: classData._id },
          });
          const attendances = response.data.attendances || response.data.data || [];
          setAttendanceEntries(Array.isArray(attendances) ? attendances : []);
        } catch (error) {
          console.error("Error fetching attendance for payroll:", error);
          setAttendanceEntries([]);
        }
      };

      fetchAttendance();
    }
  }, [classData?._id]);

  const activePayrollCycles = activeTab === "old" ? oldPayrollCycles : currentPayrollCycles;
  const currentPayrollData =
    activeTab === "old" ? currentPayrollByTab.old : currentPayrollByTab.current;
  const activeSelectedCycle =
    activeTab === "old" ? selectedCycleByTab.old : selectedCycleByTab.current;
  const showEmptyOld = activeTab === "old" && !hasOldTutorData;

  // Extract data from currentPayrollData (tutor payroll cycle data) instead of classData
  const tutorName =
    activeTab === "old"
      ? classData?.oldTutorDetails?.fullName ||
        classData?.oldTutorDetails?.name ||
        currentPayrollData?.tutorId?.fullName ||
        currentPayrollData?.tutorId?.name ||
        "N/A"
      : currentPayrollData?.tutorId?.fullName ||
        currentPayrollData?.tutorId?.name ||
        classData?.tutor?.fullName ||
        classData?.tutor?.name ||
        classData?.tutorId?.fullName ||
        classData?.tutorId?.name ||
        "N/A";
  const tutorEmail =
    activeTab === "old"
      ? classData?.oldTutorDetails?.email || currentPayrollData?.tutorId?.email || ""
      : currentPayrollData?.tutorId?.email ||
        classData?.tutor?.email ||
        classData?.tutorId?.email ||
        "";
  const tutorPhone =
    activeTab === "old"
      ? classData?.oldTutorDetails?.phone || currentPayrollData?.tutorId?.phone || ""
      : currentPayrollData?.tutorId?.phone ||
        classData?.tutor?.phone ||
        classData?.tutorId?.phone ||
        "";
  const tutorStatus = currentPayrollData?.status || "PENDING";
  const cycleType = currentPayrollData?.cycleType || "MONTHLY";

 // Calculate amounts from payroll data
// Calculate amounts from payroll data
const totalAmount = toSafeNumber(currentPayrollData?.calculatedAmount);
const bonusAmount = toSafeNumber(currentPayrollData?.bonusAmount);
const deductionAmount = toSafeNumber(currentPayrollData?.deductionAmount);
const finalAmount = totalAmount + bonusAmount - deductionAmount;

// Current selected payroll cycle classes
const totalClasses = toSafeNumber(currentPayrollData?.totalClasses);
const completedClasses = toSafeNumber(currentPayrollData?.completedClasses);

// Match class cycle by cycleNumber
const matchingClassCycle = resolveClassCycle(currentPayrollData?.cycleNumber, activeTab);

const matchingCycleSessions =
  getCycleSessions(matchingClassCycle) ||
  toSafeNumber(classData?.totalNoOfClasses) ||
  toSafeNumber(classData?.totalClasses) ||
  totalClasses ||
  1;

const matchingCycleMonthlyFee = toSafeNumber(matchingClassCycle?.monthlyFee);
const matchingCycleTutorPay = toSafeNumber(matchingClassCycle?.tutorPay);

// Parent fee per class
const baseFeeCharge =
  matchingCycleMonthlyFee > 0
    ? matchingCycleMonthlyFee / Math.max(matchingCycleSessions, 1)
    : toSafeNumber(classData?.feeCharge) ||
      toSafeNumber(classData?.perSessionFee) ||
      toSafeNumber(classData?.paymentAmount) /
        Math.max(toSafeNumber(classData?.totalNoOfClasses || classData?.totalClasses), 1);

const feeCharge = baseFeeCharge + toSafeNumber(currentPayrollData?.feeRevisionCycle?.additionalPerClass);
// Tutor salary per class
// IMPORTANT:
// current tutor => new tutor salary from classData/currentPayrollData
// old tutor => old tutor salary from oldTutorPerClassSalary / old cycle
const legacyTutorSalary =
  activeTab === "old"
    ? toSafeNumber(classData?.oldTutorPerClassSalary) ||
      toSafeNumber(currentPayrollData?.tutorPayRate) ||
      toSafeNumber(currentPayrollData?.financeId?.tutorPayRate) ||
      (matchingCycleTutorPay > 0
        ? matchingCycleTutorPay / Math.max(matchingCycleSessions, 1)
        : 0)
    : toSafeNumber(currentPayrollData?.tutorPayRate) ||
      toSafeNumber(classData?.tutorSalary) ||
      toSafeNumber(currentPayrollData?.financeId?.classSessionId?.tutorSalary) ||
      toSafeNumber(currentPayrollData?.financeId?.tutorPayRate) ||
      0;

const tutorSalary = revisedSalary(currentPayrollData, legacyTutorSalary);
// Completed classes should never exceed total classes
const resolvedTotalClasses = Math.max(totalClasses, 0);
const resolvedCompletedClassesRaw = Math.max(completedClasses, 0);
const resolvedCompletedClasses =
  resolvedTotalClasses > 0
    ? Math.min(resolvedCompletedClassesRaw, resolvedTotalClasses)
    : resolvedCompletedClassesRaw;

// Minutes
const totalMinutes = (() => {
  const durationPerSession = toSafeNumber(classData?.duration) || 60;

  if (!currentPayrollData) return 0;

  if (resolvedCompletedClasses > 0) {
    return resolvedCompletedClasses * durationPerSession;
  }

  return toSafeNumber(currentPayrollData?.totalMinutes);
})();

// Sessions summary should match package-level class management logic
const sessionsPerWeek = toSafeNumber(classData?.numberOfClassesInAWeek || classData?.weeklyClasses);
const hoursPerSession = classData?.duration ? toSafeNumber(classData.duration) / 60 : 1.5;
const noOfMonths = toSafeNumber(classData?.noOfMonths) || 1;
const totalPackageClasses = toSafeNumber(classData?.totalNoOfClasses || classData?.totalClasses);

const sessionsPerMonth =
  noOfMonths > 0 && totalPackageClasses > 0
    ? Math.round(totalPackageClasses / noOfMonths)
    : sessionsPerWeek > 0
    ? Math.round(sessionsPerWeek * 4)
    : resolvedTotalClasses;

// Payroll tab is scoped to the selected tutor payment cycle/current tutor assignment.
const totalPackageAmount = baseFeeCharge * resolvedTotalClasses + additionalParentFee(currentPayrollData);
const totalPackageTutorSalary = projectedTutorPay(currentPayrollData, tutorSalary * resolvedTotalClasses);
const totalPackageProfit = totalPackageAmount - totalPackageTutorSalary;

const totalPackageProfitPercentage =
  totalPackageAmount > 0
    ? (totalPackageProfit / totalPackageAmount) * 100
    : 0;

// Credit counter values
const cycleFeeCharge = totalPackageAmount;
const expectedTotalTutorPay = totalPackageTutorSalary;
const tutorPayEarned = earnedTutorPay(currentPayrollData, tutorSalary * resolvedCompletedClasses);

const classProgressPercentage =
  resolvedTotalClasses > 0
    ? Math.round((resolvedCompletedClasses / resolvedTotalClasses) * 100)
    : 0;

const cycleProfit = cycleFeeCharge - expectedTotalTutorPay;
  // Get status color based on status
  const getStatusColor = () => {
    switch (tutorStatus) {
      case "PAID":
        return "linear-gradient(135deg, #43a047, #26a69a)";
      case "APPROVED":
        return "linear-gradient(135deg, #2196f3, #1976d2)";
      case "PENDING":
        return "linear-gradient(135deg, #f59e0b, #f97316)";
      case "REJECTED":
      case "FAILED":
        return "linear-gradient(135deg, #ef4444, #dc2626)";
      default:
        return "linear-gradient(135deg, #6b7280, #4b5563)";
    }
  };

  // Build dynamic student table data from financeId in payroll data
  const buildStudentTableData = () => {
    // Use classData.students if available (has subjects), otherwise fallback to financeId.students
    const studentsSource = classData?.students || currentPayrollData?.financeId?.students;

    if (!studentsSource || studentsSource.length === 0) {
      return { columns: [], rows: [], totalStudents: 0 };
    }

    const columns = [
      { name: "sno", align: "center" },
      { name: "student name", align: "left" },
      { name: "school name", align: "left" },
      { name: "board", align: "center" },
      { name: "class", align: "center" },
      { name: "subject", align: "center" },
      { name: "date of birth", align: "center" },
      { name: "location city", align: "center" },
    ];

    const students = studentsSource;

    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    };

    const rows = students.map((student, index) => ({
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {index + 1}
        </ArgonTypography>
      ),
      "student name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.name || student.fullName || "N/A"}
        </ArgonTypography>
      ),
      "school name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.schoolCollegeName || student.schoolName?.name || student.schoolName || "N/A"}
        </ArgonTypography>
      ),
      class: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.class?.name || student.class || "N/A"}
        </ArgonTypography>
      ),
      subject: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.subjects && student.subjects.length > 0
            ? student.subjects.map((sub) => sub.name || sub).join(", ")
            : "N/A"}
        </ArgonTypography>
      ),
      board: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {student.board?.name || student.board || "N/A"}
        </ArgonTypography>
      ),
      "date of birth": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {formatDate(student.dob)}
        </ArgonTypography>
      ),
      "location city": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {classData?.parentId?.city || currentPayrollData?.financeId?.parentId?.city || "N/A"}
        </ArgonTypography>
      ),
    }));

    return { columns, rows, totalStudents: students.length };
  };

  // Build credit details table data
  const buildCreditDetailsTableData = (cycles = []) => {
    const columns = [
      { name: "Cycle Name", align: "left" },
      { name: "Total Classes", align: "center" },
      { name: "Completed Classes", align: "center" },
      { name: "Status", align: "center" },
    ];

    const rows = cycles.map((cycle, index) => ({
      "Cycle Name": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          Cycle {cycle.cycleNumber || index + 1}
        </ArgonTypography>
      ),
      "Total Classes": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.totalClasses || 0}
        </ArgonTypography>
      ),
      "Completed Classes": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.completedClasses || 0}
        </ArgonTypography>
      ),
      Status: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {cycle.status || "N/A"}
        </ArgonTypography>
      ),
    }));

    return { columns, rows };
  };

  const { columns, rows, totalStudents } = buildStudentTableData();
  const { columns: creditColumns, rows: creditRows } =
    buildCreditDetailsTableData(activePayrollCycles);
  const { columns: columns2, rows: rows2 } = TutorsParollViewPaymentOrderTableData;

  // Handle cycle selection change
  const handleCycleChange = (e) => {
    const cycleId = e.target.value;
    setSelectedCycleByTab((prev) => ({ ...prev, [activeTab]: cycleId }));
    const selectedCycleData = activePayrollCycles.find((c) => c._id === cycleId);
    if (selectedCycleData) {
      setCurrentPayrollByTab((prev) => ({ ...prev, [activeTab]: selectedCycleData }));
    }
  };

  const handleTabChange = (event, value) => {
    setActiveTab(value);
  };

  return (
    <ArgonBox mb={3}>
      <Card
        sx={{
          mt: 3,
          borderRadius: "20px",
          backgroundColor: "white",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          border: "1px solid #f0f0f0",
          p: 3,
        }}
      >
        {/* Title */}
        <ArgonBox mb={3}>
          <ArgonTypography variant="h5" fontWeight="bold" color="dark">
            {"Tutor's Payroll Management"}
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text">
            {"Manage tutor status, cycle, and payroll details"}
          </ArgonTypography>
        </ArgonBox>

        <ArgonBox mb={3}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Current Tutor" value="current" />
            <Tab label="Old Tutor" value="old" disabled={!hasOldTutorData} />
          </Tabs>
        </ArgonBox>

        {/* Tutor Info Row */}
        <ArgonBox display="flex" alignItems="center" justifyContent="flex-start" gap={4} mb={3}>
          <ArgonTypography variant="body1" fontWeight="medium" color="dark">
            <strong>Tutor&apos;s Name:</strong> {tutorName}
          </ArgonTypography>

          <ArgonBox
            display="flex"
            alignItems="center"
            px={2}
            py={0.5}
            sx={{
              background: getStatusColor(),
              borderRadius: "12px",
              boxShadow: "0 4px 10px rgba(67,160,71,0.25)",
            }}
          >
            <ArgonTypography
              variant="caption"
              fontWeight="bold"
              color="white"
              sx={{ fontSize: "13px", letterSpacing: "0.5px" }}
            >
              {tutorStatus}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>

        {/* Cycle Selector */}
        <ArgonBox display="flex" alignItems="center" gap={2}>
          <Dropdown
            label="Select Cycle"
            options={activePayrollCycles.map((c) => ({
              value: c._id,
              label: `Cycle ${c.cycleNumber} (${c.status})`,
            }))}
            value={activeSelectedCycle}
            onChange={handleCycleChange}
          />
          <ArgonTypography variant="body2" color="text" fontWeight="medium">
            Cycle Type: <strong>{cycleType}</strong>
          </ArgonTypography>
          <ArgonTypography variant="body2" color="text" fontWeight="medium">
            Total Cycles: <strong>{activePayrollCycles.length}</strong>
          </ArgonTypography>
        </ArgonBox>
      </Card>

      {showEmptyOld ? (
        <Card
          sx={{
            mt: 3,
            borderRadius: "16px",
            backgroundColor: "white",
            boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
            border: "1px solid #f0f0f0",
          }}
        >
          <ArgonBox p={3} textAlign="center">
            <ArgonTypography variant="body1" color="text" fontWeight="medium">
              Old tutor payroll data is not available for this class.
            </ArgonTypography>
          </ArgonBox>
        </Card>
      ) : (
        <>
          <ArgonBox py={3}>
            <ArgonTypography variant="body1" fontWeight="medium" sx={{ px: 3 }} mb={2}>
              Student List ({totalStudents} Students)
            </ArgonTypography>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns} rows={rows} />
            </ArgonBox>
          </ArgonBox>

          <Card
            sx={{
              mt: 3,
              borderRadius: "16px",
              backgroundColor: "white",
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
            }}
          >
            <ArgonBox p={3}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={3}>
                <ArgonBox
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    p: 1.2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
                  }}
                >
                  {/* <Payment sx={{ color: "#344767", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                    Package
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="text">
                    Package pricing and tutor salary details
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={3}>
                {/* Hourly Rates */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                      Class Rates
                    </ArgonTypography>
                    <ArgonBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Fee Charge per class
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                        ₹ {Math.round(feeCharge)}
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Tutor Salary per class
                      </ArgonTypography>
                      <ArgonTypography variant="body2" fontWeight="medium" color="dark">
                        ₹ {Math.round(tutorSalary)}
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Sessions */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                      Sessions
                    </ArgonTypography>
                    <ArgonBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={1}
                    >
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Sessions in a week
                      </ArgonTypography>
                      <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                        {sessionsPerWeek} sessions |{" "}
                        {Math.round(sessionsPerWeek * hoursPerSession * 10) / 10} hours
                      </ArgonTypography>
                    </ArgonBox>
                    <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                      <ArgonTypography variant="body2" color="text" fontWeight="medium">
                        Sessions in a month
                      </ArgonTypography>
                      <ArgonTypography variant="caption" fontWeight="medium" color="dark">
                        {sessionsPerMonth} sessions |{" "}
                        {Math.round(sessionsPerMonth * hoursPerSession * 10) / 10} hours
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Total Package Summary */}
                <Grid item xs={12} md={4}>
                  <ArgonBox
                    sx={{
                      backgroundColor: "white",
                      borderRadius: "12px",
                      p: 2.5,
                      boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                      border: "1px solid #f5f5f5",
                      height: "100%",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={2}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                        Total Package Summary
                      </ArgonTypography>
                    </ArgonBox>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Total Fee
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            ₹ {Math.round(totalPackageAmount)}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Payscale
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                            ₹ {Math.round(totalPackageTutorSalary)}
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                      <Grid item xs={4}>
                        <ArgonBox textAlign="center">
                          <ArgonTypography variant="caption" color="text">
                            Profit
                          </ArgonTypography>
                          <ArgonTypography variant="h6" fontWeight="bold" color="success">
                            ₹ {Math.round(totalPackageProfit)} (
                            {Math.round(totalPackageProfitPercentage)}%)
                          </ArgonTypography>
                        </ArgonBox>
                      </Grid>
                    </Grid>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Tutor Info */}
              <ArgonBox mt={3}>
                <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                  Tutor Name - {tutorName}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="text">
                  Email: {tutorEmail} | Phone: {tutorPhone}
                </ArgonTypography>
              </ArgonBox>
            </ArgonBox>
          </Card>

          <Card
            sx={{
              mt: 3,
              borderRadius: "16px",
              backgroundColor: "white",
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
            }}
          >
            <ArgonBox p={3}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <ArgonTypography variant="h5" fontWeight="bold" color="dark">
                  Attendance Overview
                </ArgonTypography>
              </ArgonBox>

              {/* Progress Section */}
              <Grid container spacing={3} mb={3}>
                <Grid item xs={12} md={5}>
                  <ArgonBox>
                    {(() => {
                      const completed = resolvedCompletedClasses;
                      const total = resolvedTotalClasses;
                      const percentage = total > 0 ? (completed / total) * 100 : 0;

                      return (
                        <>
                          <ArgonTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                            No. Of Sessions Completed {completed} / {total}
                          </ArgonTypography>

                          {/* Custom Progress Bar */}
                          <ArgonBox
                            sx={{
                              width: "100%",
                              height: 12,
                              backgroundColor: "#f0f0f0",
                              borderRadius: "6px",
                              overflow: "hidden",
                              position: "relative",
                            }}
                          >
                            <ArgonBox
                              sx={{
                                width: `${percentage}%`,
                                height: "100%",
                                backgroundColor: "#1976d2",
                                borderRadius: "6px",
                                transition: "width 0.3s ease",
                              }}
                            />
                          </ArgonBox>

                          <ArgonTypography
                            variant="caption"
                            color="dark"
                            fontWeight="medium"
                            mt={1}
                          >
                            {Math.round(percentage)}%
                          </ArgonTypography>
                        </>
                      );
                    })()}
                  </ArgonBox>
                </Grid>

                <Grid item xs={12} md={2}></Grid>

                <Grid item xs={12} md={5}>
                  <ArgonBox>
                    <ArgonTypography variant="body2" fontWeight="bold" color="text" mb={1}>
                      Remaining Sessions
                    </ArgonTypography>
                    <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                      {Math.max(resolvedTotalClasses - resolvedCompletedClasses, 0)}
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
              </Grid>

              {/* Expiry Warning */}
              {(() => {
                const remaining = totalClasses - resolvedCompletedClasses;
                const thresholdPercentage = 75;
                const thresholdSessions = Math.ceil((totalClasses * thresholdPercentage) / 100);
                const sessionsNeeded = Math.max(0, thresholdSessions - resolvedCompletedClasses);
                const completionPercentage =
                  totalClasses > 0
                    ? Math.round((resolvedCompletedClasses / totalClasses) * 100)
                    : 0;

                if (completionPercentage >= thresholdPercentage) {
                  return (
                    <ArgonBox
                      mt={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        width: { xs: "100%", md: "fit-content" },
                        margin: "16px auto 0",
                        px: { xs: 2, md: 4 },
                        border: "2px solid #4caf50",
                        borderRadius: "12px",
                        p: 1.5,
                        backgroundColor: "#f0fff4",
                      }}
                    >
                      <i
                        className="fa-solid fa-check-circle"
                        style={{ color: "green", fontSize: "22px", marginRight: "12px" }}
                      ></i>
                      <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                        {"Tutor's salary is now due for payment. ₹"}
                        {Math.round(tutorPayEarned)}
                        {" earned so far."}
                      </ArgonTypography>
                    </ArgonBox>
                  );
                }

                return (
                  <ArgonBox
                    mt={2}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      width: { xs: "100%", md: "fit-content" },
                      margin: "16px auto 0",
                      px: { xs: 2, md: 4 },
                      border: "2px solid #f44336",
                      borderRadius: "12px",
                      p: 1.5,
                      backgroundColor: "#fff5f5",
                    }}
                  >
                    <i
                      className="fa-solid fa-triangle-exclamation"
                      style={{ color: "red", fontSize: "22px", marginRight: "12px" }}
                    ></i>
                    <ArgonTypography
                      variant="body2"
                      fontWeight="bold"
                      color="dark"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {`Tutor's salary will be due after ${sessionsNeeded} more session${
                        sessionsNeeded !== 1 ? "s" : ""
                      } (${completionPercentage}% completed, ${thresholdPercentage}% required).`}
                    </ArgonTypography>
                  </ArgonBox>
                );
              })()}
            </ArgonBox>
          </Card>

          <Card
            sx={{
              mt: 3,
              borderRadius: "20px",
              backgroundColor: "white",
              boxShadow: "0 15px 35px rgba(0,0,0,0.08)",
              border: "1px solid #f0f0f0",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative Background Element */}
            <ArgonBox
              sx={{
                position: "absolute",
                top: -40,
                right: -40,
                width: 120,
                height: 120,
                background: "linear-gradient(45deg, #667eea, #764ba2)",
                borderRadius: "50%",
                opacity: 0.05,
              }}
            />

            <ArgonBox p={4}>
              {/* Header */}
              <ArgonBox display="flex" alignItems="center" gap={2} mb={4}>
                <ArgonBox
                  sx={{
                    background: "linear-gradient(45deg, #667eea, #764ba2)",
                    borderRadius: "12px",
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                  }}
                >
                  {/* <AccountBalance sx={{ color: "white", fontSize: "22px" }} /> */}
                </ArgonBox>
                <ArgonBox>
                  <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                    Credit Counter
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="#6c757d">
                    Track credits, usage and payments
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>

              <Grid container spacing={4}>
                {/* Progress Bar Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "#f8f9fa",
                      borderRadius: "16px",
                      p: 3,
                      textAlign: "center",
                      border: "1px solid #e9ecef",
                    }}
                  >
                    <ArgonBox
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      mb={3}
                      justifyContent="center"
                    >
                      {/* <DonutLarge sx={{ color: "#2196F3", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Usage Progress
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        width: 180,
                        height: 180,
                        margin: "0 auto",
                        mb: 2,
                      }}
                    >
                      <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%" }}>
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{ stroke: "#e0e0e0" }}
                        />
                        <path
                          d="M 50,50 m 0,-46 a 46,46 0 1 1 0,92 a 46,46 0 1 1 0,-92"
                          strokeWidth="6"
                          fillOpacity="0"
                          style={{
                            stroke: "url(#gradient)",
                            strokeDasharray: "289.027px, 289.027px",
                            strokeDashoffset: `${
                              289.027 - (289.027 * classProgressPercentage) / 100
                            }px`,
                            strokeLinecap: "round",
                          }}
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" style={{ stopColor: "#667eea", stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: "#764ba2", stopOpacity: 1 }} />
                          </linearGradient>
                        </defs>
                        <text
                          x="50"
                          y="45"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#2c3e50", fontSize: "12px", fontWeight: "bold" }}
                        >
                          {classProgressPercentage}%
                        </text>
                        <text
                          x="50"
                          y="58"
                          textAnchor="middle"
                          alignmentBaseline="central"
                          style={{ fill: "#6c757d", fontSize: "6px" }}
                        >
                          Completed
                        </text>
                      </svg>
                    </ArgonBox>

                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold" color="#2c3e50">
                        ₹{Math.round(tutorPayEarned)} / ₹{Math.round(expectedTotalTutorPay)}
                      </ArgonTypography>
                      <ArgonTypography variant="body2" color="#6c757d">
                        Tutor Pay Progress
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="success.main" fontWeight="medium">
                        {completedClasses} / {totalClasses} Classes Completed
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </Grid>

                {/* Table Section */}
                <Grid item xs={12} md={6}>
                  <ArgonBox
                    sx={{
                      background: "white",
                      borderRadius: "16px",
                      p: 3,
                      boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
                      border: "1px solid #f0f0f0",
                    }}
                  >
                    <ArgonBox display="flex" alignItems="center" gap={1.5} mb={3}>
                      {/* <TableChart sx={{ color: "#4CAF50", fontSize: "20px" }} /> */}
                      <ArgonTypography variant="h6" fontWeight="bold" color="#2c3e50">
                        Credit Details
                      </ArgonTypography>
                    </ArgonBox>

                    <ArgonBox
                      sx={{
                        "& .MuiTableRow-root:not(:last-child)": {
                          "& td": {
                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                              `${borderWidth[1]} solid ${borderColor}`,
                          },
                        },
                      }}
                    >
                      <Table columns={creditColumns} rows={creditRows} />
                    </ArgonBox>
                  </ArgonBox>
                </Grid>
              </Grid>

              <Grid container spacing={3} mt={2} alignItems="stretch">
                {/* Account Summary */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: "16px",
                      backgroundColor: "white",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ArgonBox p={3} flexGrow={1}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                        Account Summary
                      </ArgonTypography>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Balance:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="primary">
                          ₹ {Math.round(expectedTotalTutorPay)}{" "}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Total Classes:
                        </ArgonTypography>
                        <Chip
                          label={currentPayrollData?.totalClasses || 0}
                          size="small"
                          sx={{
                            bgcolor: "primary.main",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Payment Details */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: "16px",
                      backgroundColor: "white",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ArgonBox p={3} flexGrow={1}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                        Payment Details
                      </ArgonTypography>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Base Amount:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="dark">
                          ₹{currentPayrollData?.calculatedAmount || 0}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Bonus Amount:
                        </ArgonTypography>
                        <Chip
                          label={`₹${currentPayrollData?.bonusAmount || 0}`}
                          size="small"
                          sx={{
                            bgcolor: "success.main",
                            color: "white",
                            fontWeight: "600",
                          }}
                        />
                      </ArgonBox>

                      <ArgonBox display="flex" justifyContent="space-between" mb={1.5}>
                        <ArgonTypography variant="body2" color="text" fontWeight="medium">
                          Deduction:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" fontWeight="bold" color="error.dark">
                          ₹{currentPayrollData?.deductionAmount || 0}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox
                        display="flex"
                        justifyContent="space-between"
                        sx={{
                          borderTop: "2px solid #e0e0e0",
                          pt: 1.5,
                          mt: 1,
                        }}
                      >
                        <ArgonTypography variant="body2" color="text" fontWeight="bold">
                          Final Amount:
                        </ArgonTypography>
                        <ArgonTypography variant="h6" fontWeight="bold" color="success.main">
                          ₹{finalAmount}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Cycle Summary */}
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      borderRadius: "16px",
                      backgroundColor: "white",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <ArgonBox p={3} flexGrow={1}>
                      <ArgonTypography variant="h6" fontWeight="bold" color="dark" mb={2}>
                        Cycle Summary
                      </ArgonTypography>

                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <ArgonBox display="flex" justifyContent="space-between">
                            <ArgonTypography variant="body2" color="text" fontWeight="medium">
                              Cycle Type:
                            </ArgonTypography>
                            <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                              {currentPayrollData?.cycleType || "N/A"}
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>

                        <Grid item xs={12}>
                          <ArgonBox display="flex" justifyContent="space-between">
                            <ArgonTypography variant="body2" color="text" fontWeight="medium">
                              Total Minutes:
                            </ArgonTypography>
                            <ArgonTypography variant="h6" fontWeight="bold" color="dark">
                              {totalMinutes} mins
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>

                        <Grid item xs={12}>
                          <ArgonBox display="flex" justifyContent="space-between">
                            <ArgonTypography variant="body2" color="text" fontWeight="medium">
                              Final Amount:
                            </ArgonTypography>
                            <ArgonTypography variant="h6" fontWeight="bold" color="success.main">
                              ₹ {finalAmount}
                            </ArgonTypography>
                          </ArgonBox>
                        </Grid>
                      </Grid>
                    </ArgonBox>
                  </Card>
                </Grid>
              </Grid>

              {/* <ArgonBox mt={4}>
            <ArgonTypography mb={2} variant="body2" fontWeight="medium" color="dark">
              Total Payment Orders
            </ArgonTypography>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              <Table columns={columns2} rows={rows2} />
            </ArgonBox>
          </ArgonBox> */}
            </ArgonBox>
          </Card>
        </>
      )}
    </ArgonBox>
  );
}

payroll.propTypes = {
  classData: PropTypes.shape({
    students: PropTypes.array,
    tutor: PropTypes.shape({
      name: PropTypes.string,
      fullName: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
    }),
    oldTutorDetails: PropTypes.shape({
      _id: PropTypes.string,
      fullName: PropTypes.string,
      email: PropTypes.string,
      phone: PropTypes.string,
      profileImage: PropTypes.string,
    }),
    oldTutorPaymentCycles: PropTypes.array,
    oldTutorCalculatedAmount: PropTypes.number,
    oldTutorCompletedClasses: PropTypes.number,
    oldTutorPaidAmount: PropTypes.number,
    oldTutorPaidCyclesCount: PropTypes.number,
    oldTutorTotalCyclesCount: PropTypes.number,
    oldTutorPerClassSalary: PropTypes.number,
    oldTutorSalaryStatus: PropTypes.string,
    oldTutorMargin: PropTypes.number,
    oldTutorReplacedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    parent: PropTypes.shape({
      city: PropTypes.string,
    }),
    cycles: PropTypes.array,
    status: PropTypes.string,
    cycleType: PropTypes.string,
    calculatedAmount: PropTypes.number,
    bonusAmount: PropTypes.number,
    deductionAmount: PropTypes.number,
    monthlyTutorSalary: PropTypes.number,
    completedClasses: PropTypes.number,
    totalNoOfClasses: PropTypes.number,
    totalClasses: PropTypes.number,
    feeCharge: PropTypes.number,
    tutorPayRate: PropTypes.number,
    tutorSalary: PropTypes.number,
    totalTutorCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    totalTutorPay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    grossMarginAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    grossMarginPercentage: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    paymentAmount: PropTypes.number,
    numberOfClassesInAWeek: PropTypes.number,
    weeklyClasses: PropTypes.number,
    duration: PropTypes.number,
    noOfMonths: PropTypes.number,
    totalMinutes: PropTypes.number,
    paymentType: PropTypes.string,
  }),
  payrollCycles: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      tutorId: PropTypes.shape({
        _id: PropTypes.string,
        fullName: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string,
      }),
      financeId: PropTypes.shape({
        _id: PropTypes.string,
        parentId: PropTypes.shape({
          _id: PropTypes.string,
          city: PropTypes.string,
        }),
        students: PropTypes.array,
        classSessionId: PropTypes.shape({
          _id: PropTypes.string,
          numberOfClassesInAWeek: PropTypes.number,
        }),
        totalAmount: PropTypes.number,
        tutorPayRate: PropTypes.number,
        paymentType: PropTypes.string,
      }),
      cycleNumber: PropTypes.number,
      cycleStartDate: PropTypes.string,
      cycleEndDate: PropTypes.string,
      totalClasses: PropTypes.number,
      completedClasses: PropTypes.number,
      totalMinutes: PropTypes.number,
      calculatedAmount: PropTypes.number,
      hasFeeRevision: PropTypes.bool,
      revisedPerClassSalary: PropTypes.number,
      projectedTutorCost: PropTypes.number,
      feeRevisionCycle: PropTypes.object,
      bonusAmount: PropTypes.number,
      deductionAmount: PropTypes.number,
      status: PropTypes.string,
      cycleType: PropTypes.string,
    })
  ),
};

export default payroll;
